import React, {Fragment, useContext, useEffect, useState} from "react";

import RevisionTree from "./RevisionTree";
import {LoadingIcon} from "../../Common";

import * as ApiCalls from "../../ApiCalls";

import {azureTokenContext, messageBoxContext, userIdContext, userRolesContext} from "../../../../App";

import {Dialog, DialogPanel, Transition, TransitionChild} from '@headlessui/react';
import {PlusIcon, XMarkIcon} from '@heroicons/react/24/outline';

import "../../../../style/PriorityTableStyles/RevisionModal.css";
import {StatusEnum} from "../../../../Types/StatusEnum";
import {TaskType} from "../../../../Types/Task";

import moment from "moment";
import "moment/locale/nb";

export default function RevisionModal({
                                          isOpen,
                                          setIsOpen,
                                          task,
                                          setAllTasks,
                                          setSelectedTasks
                                      }) {

    // Fast revision : its a simplified proccess where author approves the notes without any changes so the status set to ready for review. fast revision is also by defalut a main revision
    // Main revision: The revision is about all of the document.
    // Part revision : The author choose a part of the document to make a workitem.

    const [checkedIds, setCheckedIds] = useState([]);
    const [isPendingMakeRevision, setIsPendingMakeRevision] = useState(true);
    const [isPartRevisionExpanded, setIsPartRevisionExpanded] = useState(false);

    let setMessageBoxProp = useContext(messageBoxContext);
    const azureToken = useContext(azureTokenContext);
    const userId = useContext(userIdContext);
    const userRoles = useContext(userRolesContext);

    useEffect(() => {
        if (isOpen === true) {
            // Modal just opened
            setCheckedIds([]);
            setIsPendingMakeRevision(false);
            // setIsPartRevisionExpanded(false);
        }
    }, [isOpen]);

    const handleCloseModal = () => setIsOpen(false);

    const setMessageBoxPropFunc = (msg) => {
        // since it is invocated by changes in prop, I want to make sure that it changes
        setMessageBoxProp("");
        setTimeout(() => {
            setMessageBoxProp(msg);
        }, 10);
    };

    const log = (level, message, method, workItemId, userRole) => {
        ApiCalls.log(level, message, method, workItemId, userRole, userId, azureToken);
    };

    const logMetric = (category, subCategory, status, tag, message, workItemId, userRole) => {
        ApiCalls.logMetric(category, subCategory, status, tag, message, workItemId, userRole, userId, azureToken);
    };

    const setRevisionFailedLog = (task) => {
        log("ERROR", "Revision could not be set", "AuthorsPortal", task.WorkItemId, task.Role);
        logMetric("Failed revision set", "", "Failed", "", "Failed set revision from Authorsportal", task.WorkItemId, task.Role);
    };

    const getRevisionTree = () => {
        return <RevisionTree task={task} checkedIds={checkedIds} setCheckedIds={setCheckedIds}/>;
    };

    const sentRevisionButtonClicked = (isMainRevision, isFastRevision) => {
        setIsPendingMakeRevision(true);

        const data = {
            Source: task.Source,
            DocumentId: task.DocumentId,
            IsFastRevision: isFastRevision,
            IsMainRevision: isMainRevision,
            SubIds: task.PartIds,
            UserId: userId,
            ActorUserId: userId,
        };

        ApiCalls.makeRevision(data, azureToken)
            .then((response) => {
                if (response.Success === true) {
                    isFastRevision
                        ? setMessageBoxPropFunc("successMakingRevision(Fast Revision)")
                        : setMessageBoxPropFunc("successMakingRevision");
                    updateTasks(response.WorkItem, isFastRevision)

                    log("INFO", `Revision set for WorkItemId: ${task.WorkItemId}`, "AuthorsPortal", task.WorkItemId, task.Role);
                    logMetric(
                        "Revision set",
                        "",
                        isFastRevision ? "ReadyForReview" : "Init",
                        "",
                        "Set revision from Authorsportal",
                        task.WorkItemId,
                        task.Role
                    );
                } else {
                    setMessageBoxPropFunc("generalFailMessage");
                    setRevisionFailedLog(task);
                }
            })
            .catch(() => {
                setMessageBoxPropFunc("generalFailMessage");
                setRevisionFailedLog(task);
            })
            .finally(() => {
                handleCloseModal();
                setIsPendingMakeRevision(false);
            });
    };

    const updateTasks = (wi, isFastRevision: boolean) => {
        let newTask: TaskType = {...task};
        newTask.Status = isFastRevision ? StatusEnum.readyForReview : StatusEnum.init;
        newTask.WorkItemStatus = isFastRevision ? StatusEnum.readyForReview : StatusEnum.init;
        newTask.DueDate = isFastRevision ? "" : moment.utc().add(1, 'month').format();
        newTask.Description = isFastRevision ? "Vent på innspill fra fagredaktør og korrekturleser" : "Må gjennomgås";
        newTask.WorkItemId = wi.WorkItemId;
        if (wi.LinkToDocument) newTask.LinkToDocument = wi.LinkToDocument;
        if (wi.SharepointPath) newTask.SharepointPath = wi.SharepointPath;
        if (wi.SharepointFolder) newTask.SharepointFolder = wi.SharepointFolder;
        newTask.AuthorCreatedWI = true;

        // Update the task in allTasks and selectedTasks
        setAllTasks((prevTasks) =>
            prevTasks.map((t) =>
                t.TaskId === newTask.TaskId ? {...t, ...newTask} : t
            )
        );
        setSelectedTasks((prevTasks) =>
            prevTasks.map((t) =>
                t.TaskId === newTask.TaskId ? {...t, ...newTask} : t
            )
        );


    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-10" onClose={handleCloseModal}>
                <TransitionChild
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black bg-opacity-30"/>
                </TransitionChild>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <TransitionChild
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <DialogPanel
                                className="w-full max-w-5xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all max-h-[85vh] overflow-y-auto">
                                <h3 className="text-2xl font-medium leading-6 mb-2">Oppdatering av
                                    noter</h3>
                                <div className="mt-3">
                                    <p className="text-sm mb-4 ml-3">
                                        Det er viktig for våre brukere at Karnov-kommentarene er oppdaterte. Vi ber deg
                                        derfor om å
                                        gjennomgå kommentarene dine årlig og påse at de er oppdaterte. Det er også
                                        ønskelig
                                        med
                                        løpende oppdateringer av enkeltnoter ved behov (for eksempel på grunn av en
                                        lovendring eller
                                        ny rettspraksis).
                                    </p>

                                    <h6 className="text-xl font-medium mb-1 mt-4">Notene mine må endres før
                                        republisering</h6>
                                    <p className="text-sm mb-3 ml-3">
                                        Klikk «Opprett» for å få et oppdateringsmanus med alle notene dine. Endringer
                                        føres
                                        inn med
                                        rød skriftfarge (spor endringer skal ikke benyttes). Lever oppdateringsmanuset
                                        ved å
                                        klikke
                                        «Sett til vurdering» i Ktext når du er ferdig. For hjelp med å komme i gang
                                        se{" "}
                                        <a className="text-blue-600 hover:underline" href="https://support.karnov.no"
                                           rel="noreferrer noopener"
                                           target="_blank">
                                            denne guiden
                                        </a>
                                        . Alternativt send e-post til{" "}
                                        <a className="text-blue-600 hover:underline"
                                           href="mailto:support@karnovgroup.no"
                                           rel="noreferrer noopener"
                                           target="_blank">
                                            support@karnovgroup.no
                                        </a>
                                        . Forlaget tar en siste gjennomgang og republiserer alle noter med ny
                                        datostempling
                                        i
                                        Lovdata Pro. Siden alle notene får ny datostempling og blir bekreftet à jour, er
                                        det
                                        viktig
                                        å påse at innholdet i alle noter er oppdatert.
                                    </p>

                                    <button
                                        onClick={() => sentRevisionButtonClicked(true, false)}
                                        disabled={isPendingMakeRevision}
                                        className="inline-flex justify-center rounded-md border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed items-center"
                                    >
                                        {isPendingMakeRevision && (
                                            <LoadingIcon/>
                                        )}
                                        Opprett
                                    </button>

                                    <h6 className="text-xl font-medium mb-1 mt-4">Ingen endring</h6>
                                    <p className="text-sm mb-3 ml-3">
                                        Velg dette alternativet dersom alle notene dine kan republiseres med ny dato
                                        uten
                                        endringer.
                                        Vær oppmerksom på at det likevel kan være nødvendig å gjennomgå og oppdatere
                                        henvisninger i
                                        notene, selv om kilden du skriver til ikke har endret seg. Dette gjelder for
                                        eksempel
                                        henvisninger til satser, opphevede bestemmelser eller eldre utgaver av juridisk
                                        litteratur.
                                    </p>

                                    <div>
                                        <button
                                            onClick={() => sentRevisionButtonClicked(true, true)}
                                            disabled={isPendingMakeRevision}
                                            className="inline-flex justify-center rounded-md border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed items-center"
                                        >
                                            {isPendingMakeRevision && (
                                                <LoadingIcon/>
                                            )}
                                            Ingen endring
                                        </button>
                                    </div>

                                    {userRoles.includes("KEmployee") && (
                                        <>
                                            <div className="mt-8">
                                                <button
                                                    className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-gray-300 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    onClick={() => setIsPartRevisionExpanded(!isPartRevisionExpanded)}
                                                >
                                                    {isPartRevisionExpanded ? <XMarkIcon className="w-5 h-5"/> :
                                                        <PlusIcon className="w-5 h-5"/>}
                                                </button>
                                                <h6 className="inline ml-3 text-base font-medium">
                                                    Velg noter som skal oppdateres (bare Karnov-ansatte kan se dette)
                                                </h6>
                                            </div>
                                            {isPartRevisionExpanded && (
                                                <div className="mt-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                                                    <div>{getRevisionTree()}</div>
                                                    <div>
                                                        <button
                                                            onClick={() => sentRevisionButtonClicked(false, false)}
                                                            disabled={checkedIds.length === 0 || isPendingMakeRevision}
                                                            className="inline-flex justify-center rounded-md border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed items-center"
                                                        >
                                                            {isPendingMakeRevision && (
                                                                <LoadingIcon/>
                                                            )}
                                                            Opprett
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}

                                    <div className="flex justify-between items-start mt-8">
                                        <div className="flex-1">
                                            <h6 className="text-xl font-medium mb-2">Jeg ønsker å oppdatere
                                                enkeltnoter</h6>
                                            <p className="text-sm ml-3 text-gray-700">
                                                Ta kontakt med{" "}
                                                <a className="text-blue-600 hover:underline"
                                                   href="mailto:support@karnovgroup.no"
                                                   rel="noreferrer noopener"
                                                   target="_blank">
                                                    support@karnovgroup.no
                                                </a>{" "}
                                                dersom du kun ønsker å oppdatere enkeltnoter. Angi i emnefeltet at det
                                                gjelder en
                                                enkeltoppdatering utenom den årlige gjennomgangen av alle notene.
                                            </p>
                                        </div>
                                        <div className="ml-4">
                                            <button
                                                onClick={handleCloseModal}
                                                className="inline-flex justify-center rounded-md border border-transparent bg-gray-100 px-4 py-2 text-sm font-medium hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2"
                                            >
                                                Lukk vinduet
                                            </button>
                                        </div>
                                    </div>
                                </div>

                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}
